import React, { useState } from 'react';
import { XIcon, UsersIcon, FileTextIcon, CopyIcon, ChevronDownIcon, ChevronUpIcon, SparklesIcon, CalendarIcon, BookOpenIcon, ShareIcon, EditIcon, PlayIcon, GraduationCapIcon, BookmarkIcon, HeartIcon, ListChecksIcon, PlusIcon, ListIcon } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
interface Assignment {
  id: number;
  label: string;
  distributionType: 'link' | 'group' | 'classroom';
  status: 'Active' | 'Closed';
  responseCount: number;
  link: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  timeLimit?: number;
  groupName?: string;
}
interface PreviewModalProps {
  assessment: any;
  onClose: () => void;
  userRole?: 'teacher' | 'student';
  isSaved?: boolean;
  onToggleSave?: (id: number) => void;
  assignments?: Assignment[];
  onOpenAssignmentsPanel?: (autoCreate: boolean) => void;
}
const PreviewModal = ({
  assessment,
  onClose,
  userRole = 'teacher',
  isSaved = false,
  onToggleSave,
  assignments = [],
  onOpenAssignmentsPanel
}: PreviewModalProps) => {
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const getTagColor = (index: number) => {
    const colors = ['bg-[#2563EB] bg-opacity-10 text-[#2563EB] border border-[#2563EB] border-opacity-20', 'bg-[#00B75F] bg-opacity-10 text-[#00B75F] border border-[#00B75F] border-opacity-20', 'bg-[#FF9D33] bg-opacity-10 text-[#FF9D33] border border-[#FF9D33] border-opacity-20', 'bg-[#FF6CA1] bg-opacity-10 text-[#FF6CA1] border border-[#FF6CA1] border-opacity-20', 'bg-[#8B5CF6] bg-opacity-10 text-[#8B5CF6] border border-[#8B5CF6] border-opacity-20'];
    return colors[index % colors.length];
  };
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy':
        return 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]';
      case 'Medium':
        return 'bg-[#FF9D33] bg-opacity-10 text-[#FF9D33]';
      case 'Hard':
        return 'bg-[#FF6CA1] bg-opacity-10 text-[#FF6CA1]';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };
  const questionsToShow = showAllQuestions ? assessment.questionsList : assessment.questionsList?.slice(0, 5);
  const isStudent = userRole === 'student';
  const activeAssignmentsCount = assignments.filter(a => a.status === 'Active').length;
  const handleSaveClick = () => {
    if (onToggleSave) {
      onToggleSave(assessment.id);
    }
  };
  return <Tooltip.Provider delayDuration={200} data-id="element-1878">
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1879">
        <div className="bg-surface-elevated rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-custom" data-id="element-1880">
          {/* Header */}
          <div className="sticky top-0 bg-surface-elevated border-b border-custom p-4 flex items-start justify-between rounded-t-2xl z-10" data-id="element-1881">
            <div className="flex-1 pr-4" data-id="element-1882">
              <div className="flex items-center gap-3 mb-2" data-id="element-1883">
                <span className="text-3xl" data-id="element-1884">{assessment.icon}</span>
                <h2 className="text-xl font-bold text-text-primary" data-id="element-1885">
                  {assessment.title}
                </h2>
              </div>

              {/* Grade Level Badge + Subject Tags */}
              <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-1886">
                {/* Grade Level Badge */}
                {assessment.gradeLevel && <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-lg bg-[#00B75F] bg-opacity-10 text-[#00B75F] border border-[#00B75F] border-opacity-30" data-id="element-1887">
                    <GraduationCapIcon size={12} data-id="element-1888" />
                    {assessment.gradeLevel}
                  </span>}

                {/* Subject Tags */}
                {assessment.tags.map((tag: string, index: number) => <span key={index} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${getTagColor(index)}`} data-id="element-1889">
                    {tag}
                  </span>)}
              </div>

              {/* Creator and Creation Date - Combined Line */}
              <div className="flex items-center gap-2 text-text-secondary text-xs" data-id="element-1890">
                <span data-id="element-1891">
                  Created by{' '}
                  <span className="font-semibold text-text-primary" data-id="element-1892">
                    {assessment.creator}
                  </span>
                </span>
                <span data-id="element-1893">•</span>
                <div className="flex items-center gap-1.5" data-id="element-1894">
                  <CalendarIcon size={12} data-id="element-1895" />
                  <span data-id="element-1896">{assessment.createdDate || 'January 15, 2024'}</span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface transition-colors" aria-label="Close preview" data-id="element-1897">
              <XIcon size={20} className="text-text-secondary" data-id="element-1898" />
            </button>
          </div>

          {/* Horizontal Stats Bar */}
          <div className="bg-surface border-b border-custom px-4 py-3" data-id="element-1899">
            <div className="flex items-center gap-4 flex-wrap" data-id="element-1900">
              {/* Questions Badge */}
              <div className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-[#2563EB] bg-opacity-10 text-[#2563EB] border border-[#2563EB] border-opacity-30" data-id="element-1901">
                <ListChecksIcon size={14} data-id="element-1902" />
                {assessment.questions} Questions
              </div>

              {/* Uses Badge */}
              <div className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-[#FF6CA1] bg-opacity-10 text-[#FF6CA1] border border-[#FF6CA1] border-opacity-30" data-id="element-1903">
                <HeartIcon size={14} data-id="element-1904" />
                {assessment.uses || assessment.attempts} Uses
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4" data-id="element-1905">
            {/* AI Summary Card */}
            <div className="mb-3" data-id="element-1906">
              <div className="bg-gradient-to-br from-[#8B5CF6]/5 to-[#2563EB]/5 rounded-xl p-4 border border-[#8B5CF6]/20" data-id="element-1907">
                <div className="flex items-center gap-2 mb-2" data-id="element-1908">
                  <SparklesIcon size={14} className="text-[#8B5CF6]" data-id="element-1909" />
                  <h3 className="text-xs font-semibold text-text-primary uppercase tracking-wide" data-id="element-1910">
                    AI-Generated Summary
                  </h3>
                </div>
                <div className="space-y-2" data-id="element-1911">
                  {/* Learning Objectives */}
                  <div data-id="element-1912">
                    <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-0.5" data-id="element-1913">
                      Learning Objectives
                    </p>
                    <p className="text-xs text-text-primary leading-relaxed" data-id="element-1914">
                      {assessment.learningObjectives || 'Students will understand and apply fundamental concepts through interactive questions and real-world applications.'}
                    </p>
                  </div>
                  {/* Topics & Subtopics */}
                  <div data-id="element-1915">
                    <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-0.5" data-id="element-1916">
                      Topics Covered
                    </p>
                    <p className="text-xs text-text-primary leading-relaxed" data-id="element-1917">
                      {assessment.topicsCovered || 'Core concepts, practical applications, and skill development exercises'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Questions List */}
            {assessment.questionsList && assessment.questionsList.length > 0 && <div className="mb-3" data-id="element-1918">
                  <div className="flex items-center justify-between mb-3" data-id="element-1919">
                    <h3 className="text-base font-bold text-text-primary" data-id="element-1920">
                      Question Preview
                    </h3>
                    <span className="text-xs text-text-secondary" data-id="element-1921">
                      {isStudent ? 'Preview questions before starting' : 'Review all questions'}
                    </span>
                  </div>
                  <div className="space-y-2" data-id="element-1922">
                    {questionsToShow.map((question: any, index: number) => <div key={question.id} className="bg-surface rounded-lg p-3 border border-custom hover:border-[#00B75F] hover:border-opacity-30 transition-all" data-id="element-1923">
                        <div className="flex items-start gap-2 mb-1.5" data-id="element-1924">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#00B75F] bg-opacity-10 text-[#00B75F] text-xs font-bold flex items-center justify-center" data-id="element-1925">
                            {index + 1}
                          </span>
                          <div className="flex-1" data-id="element-1926">
                            <p className="text-sm text-text-primary font-medium leading-relaxed" data-id="element-1927">
                              {question.text}
                            </p>
                            {!isStudent && question.answer && <div className="bg-[#00B75F] bg-opacity-5 border border-[#00B75F] border-opacity-20 rounded-lg p-2 mt-1.5" data-id="element-1928">
                                <p className="text-xs font-semibold text-[#00B75F] mb-0.5" data-id="element-1929">
                                  Correct Answer:
                                </p>
                                <p className="text-xs text-text-primary" data-id="element-1930">
                                  {question.answer}
                                </p>
                              </div>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-7" data-id="element-1931">
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-surface-elevated border border-custom text-text-secondary" data-id="element-1932">
                            {question.type}
                          </span>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getDifficultyColor(question.difficulty)}`} data-id="element-1933">
                            {question.difficulty}
                          </span>
                        </div>
                      </div>)}
                  </div>
                  {/* Show More/Less Button */}
                  {assessment.questionsList.length > 5 && <button onClick={() => setShowAllQuestions(!showAllQuestions)} className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 bg-surface hover:bg-surface-elevated border border-custom rounded-lg text-sm font-semibold text-text-primary transition-colors" data-id="element-1934">
                      {showAllQuestions ? <>
                          Show Less
                          <ChevronUpIcon size={14} data-id="element-1935" />
                        </> : <>
                          Show All {assessment.questionsList.length} Questions
                          <ChevronDownIcon size={14} data-id="element-1936" />
                        </>}
                    </button>}
                </div>}

            {/* Action Buttons - Conditional based on user role */}
            <div className="flex flex-col sm:flex-row gap-2" data-id="element-1937">
              {isStudent ?
            // Student CTAs
            <>
                  <Tooltip.Root data-id="element-1938">
                    <Tooltip.Trigger asChild data-id="element-1939">
                      <button className="flex-1 bg-[#00B75F] text-white rounded-lg px-5 py-2.5 font-semibold hover:bg-[#00a055] transition-colors shadow-sm flex items-center justify-center gap-2 text-sm" data-id="element-1940">
                        <PlayIcon size={16} data-id="element-1941" />
                        Start Assessment
                      </button>
                    </Tooltip.Trigger>
                    <Tooltip.Portal data-id="element-1942">
                      <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-xs z-[9999]" sideOffset={8} side="top" align="center" avoidCollisions={true} collisionPadding={8} data-id="element-1943">
                        <p className="text-xs font-semibold text-text-primary mb-1" data-id="element-1944">
                          Begin Your Assessment
                        </p>
                        <p className="text-xs text-text-secondary leading-relaxed" data-id="element-1945">
                          Start this assessment now and test your knowledge.
                          Your progress will be saved automatically.
                        </p>
                        <Tooltip.Arrow className="fill-surface-elevated" data-id="element-1946" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                  <button onClick={onClose} className="px-5 py-2.5 border-2 border-custom text-text-primary rounded-lg font-semibold hover:bg-surface transition-colors text-sm" data-id="element-1947">
                    Close
                  </button>
                </> :
            // Teacher CTAs
            <>
                  {/* Assign Button Logic */}
                  {activeAssignmentsCount > 0 ? <div className="flex-1 flex rounded-lg shadow-sm overflow-hidden border-2 border-[#2563EB]" data-id="element-1948">
                      <button onClick={() => onOpenAssignmentsPanel && onOpenAssignmentsPanel(false)} className="flex-1 bg-[#2563EB] text-white px-4 py-2.5 font-semibold hover:bg-[#1d4ed8] transition-colors flex items-center justify-center gap-2 text-sm border-r border-[#1d4ed8]" data-id="element-1949">
                        <UsersIcon size={16} data-id="element-1950" />
                        Assignments
                        <span className="bg-white/20 text-white text-xs font-bold px-1.5 py-0.5 rounded-md leading-none" data-id="element-1951">
                          {activeAssignmentsCount}
                        </span>
                      </button>
                      <DropdownMenu.Root data-id="element-1952">
                        <DropdownMenu.Trigger asChild data-id="element-1953">
                          <button className="bg-[#2563EB] text-white px-3 py-2.5 hover:bg-[#1d4ed8] transition-colors flex items-center justify-center" data-id="element-1954">
                            <ChevronDownIcon size={16} data-id="element-1955" />
                          </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal data-id="element-1956">
                          <DropdownMenu.Content className="bg-surface-elevated border border-custom rounded-lg shadow-custom-lg p-1 min-w-[200px] z-[9999]" sideOffset={5} align="end" data-id="element-1957">
                            <DropdownMenu.Item className="flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-surface rounded-md cursor-pointer outline-none" onSelect={() => onOpenAssignmentsPanel && onOpenAssignmentsPanel(true)} data-id="element-1958">
                              <PlusIcon size={16} className="text-[#2563EB]" data-id="element-1959" />
                              Create New Assignment
                            </DropdownMenu.Item>
                            <DropdownMenu.Item className="flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-surface rounded-md cursor-pointer outline-none" onSelect={() => onOpenAssignmentsPanel && onOpenAssignmentsPanel(false)} data-id="element-1960">
                              <ListIcon size={16} className="text-text-secondary" data-id="element-1961" />
                              View All Assignments
                            </DropdownMenu.Item>
                          </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                      </DropdownMenu.Root>
                    </div> : <Tooltip.Root data-id="element-1962">
                      <Tooltip.Trigger asChild data-id="element-1963">
                        <button onClick={() => onOpenAssignmentsPanel && onOpenAssignmentsPanel(true)} className="flex-1 text-white rounded-lg px-5 py-2.5 font-semibold hover:bg-[#1d4ed8] transition-colors shadow-sm flex items-center justify-center gap-2 text-sm bg-[#00b75f]" data-id="element-1964">
                          <UsersIcon size={16} data-id="element-1965" />
                          Quick Share: Assignment
                        </button>
                      </Tooltip.Trigger>
                      <Tooltip.Portal data-id="element-1966">
                        <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-xs z-[9999]" sideOffset={8} side="top" align="center" avoidCollisions={true} collisionPadding={8} data-id="element-1967">
                          <p className="text-xs font-semibold text-text-primary mb-1" data-id="element-1968">
                            Create an Assignment
                          </p>
                          <p className="text-xs text-text-secondary leading-relaxed" data-id="element-1969">
                            Creates a shareable assignment link for your
                            students. You can rename it and copy the link to
                            share.
                          </p>
                          <Tooltip.Arrow className="fill-surface-elevated" data-id="element-1970" />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>}

                  <Tooltip.Root data-id="element-1971">
                    <Tooltip.Trigger asChild data-id="element-1972">
                      <button className="flex-1 bg-[#2563EB] text-white rounded-lg px-5 py-2.5 font-semibold hover:bg-[#1d4ed8] transition-colors shadow-sm flex items-center justify-center gap-2 text-sm" data-id="element-1973">
                        <CopyIcon size={16} data-id="element-1974" />
                        Duplicate & Customize
                      </button>
                    </Tooltip.Trigger>
                    <Tooltip.Portal data-id="element-1975">
                      <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-xs z-[9999]" sideOffset={8} side="top" align="center" avoidCollisions={true} collisionPadding={8} data-id="element-1976">
                        <p className="text-xs font-semibold text-text-primary mb-1" data-id="element-1977">
                          Make Your Own Copy
                        </p>
                        <p className="text-xs text-text-secondary leading-relaxed" data-id="element-1978">
                          Create a duplicate you can edit. Modify questions,
                          adjust settings, and customize to fit your needs.
                        </p>
                        <Tooltip.Arrow className="fill-surface-elevated" data-id="element-1979" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>

                  <button onClick={onClose} className="px-5 py-2.5 border-2 border-custom text-text-primary rounded-lg font-semibold hover:bg-surface transition-colors text-sm" data-id="element-1980">
                    Close
                  </button>
                </>}
            </div>
          </div>
        </div>
      </div>
    </Tooltip.Provider>;
};
export default PreviewModal;